import { PageHeader, SuspenseLoader, TabsMenu } from '@folio/components';
import { useAssertedParams } from '@folio/lib';
import { useQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  keyValueFieldsToObject,
} from 'korint-sdk';
import { useKorintConfig } from 'korint-sdk/react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { Stack } from '@folio/components/ui/flex';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';

interface IntermediaryDetailsLayoutProps {
  headerRight?: React.ReactNode;
}

export function IntermediaryDetailsLayout({ headerRight }: IntermediaryDetailsLayoutProps) {
  const { brokerageFirmId } = useAssertedParams();
  const { t } = useTranslation();
  const { korintConfig } = useKorintConfig();
  const buildBrokerTitle = korintConfig.modulesConfig?.intermediaryDetailsLayout?.buildBrokerTitle;

  const { data } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId),
    select: data => ({
      companyInfo: data.companyInfo,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
    enabled: !!buildBrokerTitle,
  });

  // The Users tab lists the firm's brokers, which only LIST_BROKERS may read.
  const { can } = useUserPermissions({ actions: ['LIST_BROKERS'] });

  const brokerName = data && buildBrokerTitle?.(data);
  const title = brokerName
    ? `${t('intermediaryDetails.title')} — ${brokerName}`
    : t('intermediaryDetails.title');

  return (
    <Stack>
      <PageHeader title={title} right={headerRight} />
      <TabsMenu
        tabs={[
          {
            label: t('intermediaryDetails.tabs.home'),
            href: `/brokerage-firm-list/${brokerageFirmId}`,
          },
          {
            label: t('intermediaryDetails.tabs.documents'),
            href: `/brokerage-firm-list/${brokerageFirmId}/view-documents`,
          },
          ...(can.LIST_BROKERS
            ? [
                {
                  label: t('intermediaryDetails.tabs.users'),
                  href: `/brokerage-firm-list/${brokerageFirmId}/users`,
                },
              ]
            : []),
        ]}
      />
      <SuspenseLoader>
        <Outlet />
      </SuspenseLoader>
    </Stack>
  );
}
